<script>
  import Popper from '../../utils/vue-popper';
  import debounce from 'throttle-debounce/debounce';
  import { addClass, removeClass, on, off } from '../../utils/dom';
  import { generateId } from '../../utils/util';
  import { insertAfter } from '../../utils/dom';
  import AriaUtils from '../../utils/aria-utils';
  import AriaDialog from '../../utils/aria-dialog'
  import { _Vue } from './index';

  export default {
    name: 'ZmTooltip',

    mixins: [Popper],

    props: {
      openDelay: {
        type: Number,
        default: 300
      },
      disabled: Boolean,
      manual: Boolean,
      effect: {
        type: String,
        default: 'light'
      },
      popperClass: String,
      content: String,
      visibleArrow: {
        default: true
      },
      transition: {
        type: String,
        default: 'zm-fade-in-linear'
      },
      popperOptions: {
        default() {
          return {
            boundariesElement: 'window',
            boundariesPadding: 10,
            gpuAcceleration: false
          };
        }
      },
      enterable: {
        type: Boolean,
        default: true
      },
      hideAfter: {
        type: Number,
        default: 0
      },
      modifiers: {
        type: Object,
        default() {
          return {
            flip: {
              enabled: false
            }
          }
        }
      },
      breakWord: Boolean
    },

    data() {
      return {
        tooltipId: `zm-tooltip-${generateId()}`,
        timeoutPending: null,
        focusing: false,
        focusInPopper: false
      };
    },
    beforeCreate() {
      if (this.$isServer) return;

      const Vue = _Vue || this.$options._base;
      this.popperVM = new Vue({
        data: { node: '' },
        router: this.$router,
        render(h) {
          return this.node;
        }
      }).$mount();
      this.debounceClose = debounce(200, () => this.handleClosePopper());
    },
    computed: {
      hasATag() {
        if (!this.$slots.content) {
          return false;
        } else {
          return Boolean(this.$refs.popper.getElementsByTagName('a').length);
        }
      }
    },
    render(h) {
      if (this.popperVM) {
        let ariaLabel = this.content;
        if (this.$slots.content) {
          const elm = this.$slots.content[0].elm;
          if (elm) {
            ariaLabel = elm.innerText || elm.textContent;
          }
        }
        this.popperVM.node = (
          <transition
            name={this.transition}
            onAfterLeave={this.doDestroy}>
            <div
              onMouseleave={() => {
                this.setExpectedState(false);
                this.debounceClose();
              }}
              onMouseenter={() => {
                this.setExpectedState(true);
              }}
              onKeyup={(event) => {
                this.popperKeyup(event);
              }}
              onFocusin={(event) => {
                this.handlePopperFocusin(event);
              }}
              onFocusout={(event) => {
                this.handlePopperFocusout(event);
              }}
              ref="popper"
              role="tooltip"
              aria-label={ariaLabel}
              id={this.tooltipId}
              aria-hidden={(this.disabled || !this.showPopper) ? 'true' : 'false'}
              v-show={!this.disabled && this.showPopper}
              class={
                ['zm-tooltip__popper', 'is-' + this.effect, this.popperClass, this.breakWord ? 'is-break-word' : '']
              }>
              {this.$slots.content || this.content}
            </div>
          </transition>);
      }

      const firstElement = this.getFirstElement();
      if (!firstElement) return null;

      const data = firstElement.data = firstElement.data || {};
      data.staticClass = this.addTooltipClass(data.staticClass);

      return firstElement;
    },

    mounted() {
      // this.showPopper = true;
      this.$nextTick(() => {
        // this.showPopper = this.value;
        if (!this.appendToBody) {
          insertAfter(this.$refs.popper, this.$el);
        }
        // if (this.hasATag) {
        //   this.lastFocusEL = AriaUtils.findLastFocusEl(this.$refs.popper)
        //   document.addEventListener('keydown', this.handleTab, true);
        // }
        // fix issue https://github.com/ElemeFE/element/issues/14424
        if (this.value && this.popperVM) {
          this.popperVM.$nextTick(() => {
            if (this.value) {
              this.updatePopper();
            }
          });
        }
      });
      this.referenceElm = this.$el;
      this.popperElm = this.$refs.popper;
      if (this.$el.nodeType === 1) {
        // if (!this.disabled) {
        //   this.$el.setAttribute('aria-describedby', this.tooltipId);
        // }
        if (!this.$el.hasAttribute('tabindex')) {
          this.$el.setAttribute('tabindex', 0);
        }
        on(this.referenceElm, 'mouseenter', this.show);
        on(this.referenceElm, 'mouseleave', this.hide);
        on(this.referenceElm, 'focus', () => {
          if (!this.$slots.default || !this.$slots.default.length) {
            this.handleFocus();
            return;
          }
          const instance = this.$slots.default[0].componentInstance;
          if (instance && instance.focus) {
            instance.focus();
          } else {
            this.handleFocus();
          }
        });
        on(this.referenceElm, 'keydown', this.handleKeyDown);
        on(this.referenceElm, 'blur', this.handleBlur);
        on(this.referenceElm, 'click', this.removeFocusing);
      }
    },
    watch: {
      focusing(val) {
        if (val) {
          addClass(this.referenceElm, 'focusing');
        } else {
          removeClass(this.referenceElm, 'focusing');
        }
      },
      showPopper(val) {
        if (!this.$el || !this.$el.setAttribute) {
          return
        }
        if (val && !this.disabled) {
          this.$el.setAttribute('aria-describedby', this.tooltipId);
        } else {
          this.$el.removeAttribute('aria-describedby');
        }
      },
      disabled(val) {
        if (!val) {
          this.$nextTick(() => {
            this.updatePopper();
          });
        }
      }
    },
    methods: {
      show() {
        this.setExpectedState(true);
        this.handleShowPopper();
      },
      hide() {
        this.setExpectedState(false);
        this.debounceClose();
      },
      handlePopperFocusin() {
        this.focusInPopper = true;
      },
      handlePopperFocusout() {
        this.focusInPopper = false;
      },
      popperKeyup(event) {
        if (event.keyCode === AriaUtils.keys.esc) {
          this.hide();
          if (this.focusAfterClosedEl) {
            this.focusAfterClosedEl.focus();
          }
        }
      },
      handleTab(event) {
        if (event.keyCode !== AriaUtils.keys.tab || event.shiftKey) {
          return;
        }
        if (this.lastFocusEL === event.target) {
          this.hide();
          // this.focusInPopper = false;
        }
      },
      handleKeyDown(event) {
        if (event.keyCode === AriaUtils.keys.space || event.keyCode === AriaUtils.keys.enter) {
          if (!this.disabled && this.showPopper) {
            this.hide();
          } else {
            if (!this.$slots.default || !this.$slots.default.length) {
              this.show();
              return;
            }
            const instance = this.$slots.default[0].componentInstance;
            if (instance && instance.focus) {
              instance.focus();
            } else {
              this.show();
            }
          }
          event.stopPropagation();
        }
      },
      handleFocus() {
        this.focusing = true;
        // this.show();
      },
      handleBlur() {
        setTimeout(() => {
          if (!this.focusInPopper) {
            this.focusing = false;
            this.hide();
          }
        }, 0);
      },
      removeFocusing() {
        this.focusing = false;
      },

      addTooltipClass(prev) {
        if (!prev) {
          return 'zm-tooltip';
        } else {
          return 'zm-tooltip ' + prev.replace('zm-tooltip', '');
        }
      },
      getFirstFocus() {
        const focusFirst = this.$refs.popper.querySelector('[focusFirst]');
        if (focusFirst) {
          return focusFirst;
        }
        return null;
      },
      handleShowPopper() {
        if (!this.expectedState || this.manual) return;
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          // if (this.$el.nodeType === 1) {
          //   this.$el.setAttribute('aria-describedby', this.tooltipId);
          // }
          this.showPopper = true;
          // if (this.hasATag) {
          //   this.focusInPopper = true;
          // }
          this.focusAfterClosedEl = document.activeElement;
          if (this.hasATag) {
            setTimeout(() => {
              this.focusInPopper = true;
              const focusAfterClosed = document.activeElement;
              const firstFocus = this.getFirstFocus();
              this.ariaDialog = new AriaDialog(this.$refs.popper, focusAfterClosed, firstFocus);
            }, 0);
          }
        }, this.openDelay);

        if (this.hideAfter > 0) {
          this.timeoutPending = setTimeout(() => {
            this.showPopper = false;
          }, this.hideAfter);
        }
      },

      handleClosePopper() {
        if (this.enterable && this.expectedState || this.manual) return;
        clearTimeout(this.timeout);

        if (this.timeoutPending) {
          clearTimeout(this.timeoutPending);
        }
        if (this.ariaDialog) {
          this.ariaDialog.closeDialog();
          this.ariaDialog = null;
          this.focusInPopper = false;
        }
        this.showPopper = false;
        // if (this.$el.nodeType === 1 && this.$el.getAttribute('aria-describedby')) {
        //   this.$el.removeAttribute('aria-describedby');
        // }
        if (this.disabled) {
          this.doDestroy();
        }
      },

      setExpectedState(expectedState) {
        if (expectedState === false) {
          clearTimeout(this.timeoutPending);
        }
        this.expectedState = expectedState;
      },

      getFirstElement() {
        const slots = this.$slots.default;
        if (!Array.isArray(slots)) return null;
        let element = null;
        for (let index = 0; index < slots.length; index++) {
          if (slots[index] && slots[index].tag) {
            element = slots[index];
          }
        }
        return element;
      }
    },

    beforeDestroy() {
      this.popperVM && this.popperVM.$destroy();
    },

    destroyed() {
      const reference = this.referenceElm;
      off(reference, 'mouseenter', this.show);
      off(reference, 'mouseleave', this.hide);
      off(reference, 'keydown', this.handleKeyDown);
      off(reference, 'blur', this.handleBlur);
      off(reference, 'click', this.removeFocusing);
      document.removeEventListener('keydown', this.handleTab, true);
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(tooltip) {
    display: inline-block;
    &:focus:not(.focusing), &:focus:hover {
      outline-width: 0;
    }
    @include e(popper) {
      position: absolute;
      border-radius: 8px;
      padding: $--tooltip-padding;
      z-index: $--index-popper;
      font-size: $--tooltip-font-size;
      line-height: 24px;
      min-width: 10px;
      max-width: 320px;

      .popper__arrow,
      .popper__arrow::after {
        position: absolute;
        display: block;
        width: 0;
        height: 0;
        border-color: transparent;
        border-style: solid;
      }

      .popper__arrow {
        border-width: $--tooltip-arrow-size;
      }

      .popper__arrow::after {
        content: " ";
        border-width: 5px;
      }

      &[x-placement^="top"] {
        margin-bottom: #{$--tooltip-arrow-size + 6px};
      }

      &[x-placement^="top"] .popper__arrow {
        bottom: -$--tooltip-arrow-size;
        border-top-color: $--tooltip-border-color;
        border-bottom-width: 0;

        &::after {
          bottom: 1px;
          margin-left: -5px;
          border-top-color: $--tooltip-fill;
          border-bottom-width: 0;
        }
      }

      &[x-placement^="bottom"] {
        margin-top: #{$--tooltip-arrow-size + 6px};
      }

      &[x-placement^="bottom"] .popper__arrow {
        top: -$--tooltip-arrow-size;
        border-top-width: 0;
        border-bottom-color: $--tooltip-border-color;

        &::after {
          top: 1px;
          margin-left: -5px;
          border-top-width: 0;
          border-bottom-color: $--tooltip-fill;
        }
      }

      &[x-placement^="right"] {
        margin-left: #{$--tooltip-arrow-size + 6px};
      }

      &[x-placement^="right"] .popper__arrow {
        left: -$--tooltip-arrow-size;
        border-right-color: $--tooltip-border-color;
        border-left-width: 0;

        &::after {
          bottom: -5px;
          left: 1px;
          border-right-color: $--tooltip-fill;
          border-left-width: 0;
        }
      }

      &[x-placement^="left"] {
        margin-right: #{$--tooltip-arrow-size + 6px};
      }

      &[x-placement^="left"] .popper__arrow {
        right: -$--tooltip-arrow-size;
        border-right-width: 0;
        border-left-color: $--tooltip-border-color;

        &::after {
          right: 1px;
          bottom: -5px;
          margin-left: -5px;
          border-right-width: 0;
          border-left-color: $--tooltip-fill;
        }
      }

      @include when(break-word) {
        word-break: break-all;
      }

      @include when(dark) {
        background: $--tooltip-fill;
        color: $--tooltip-color;
      }

      @include when(light) {
        background: $--fill-base;
        box-shadow: $--box-shadow-light;
        color: $--tooltip-light-color;
        &[x-placement^="top"] .popper__arrow {
          border-top-color: $--border-color-base;
          &::after {
            border-top-color: $--fill-base;
          }
        }
        &[x-placement^="bottom"] .popper__arrow {
          border-bottom-color: $--border-color-base;
          &::after {
            border-bottom-color: $--fill-base;
          }
        }
        &[x-placement^="left"] .popper__arrow {
          border-left-color: $--border-color-base;
          &::after {
            border-left-color: $--fill-base;
          }
        }
        &[x-placement^="right"] .popper__arrow {
          border-right-color: $--border-color-base;
          &::after {
            border-right-color: $--fill-base;
          }
        }
      }
    }
  }

</style>
